import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// --- generator ---

// sensowny zakres krawędzi (cm), żeby liczby nie były za duże
const EDGES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export function generatePrismDiagonalTask() {
  const a = EDGES[Math.floor(Math.random() * EDGES.length)];
  const b = EDGES[Math.floor(Math.random() * EDGES.length)];
  const h = EDGES[Math.floor(Math.random() * EDGES.length)];

  const a2 = a * a;
  const b2 = b * b;
  const h2 = h * h;
  const sum = a2 + b2 + h2;      // pod pierwiastkiem
  const dExact = Math.sqrt(sum); // prawdziwa wartość (zwykle niecałkowita)

  const core = { a, b, h, d: dExact, sum };

  return {
    type: "PrismDiagonalTask",
    ...core,
    data: core,
  };
}

export function generateTask() {
  return generatePrismDiagonalTask();
}

// --- helper do task / { data } ---

function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

// --- parser wyrażeń z pierwiastkiem ---

function evaluateExpression(expr) {
  if (expr == null) return NaN;

  const cleaned = String(expr)
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/,/g, "."); // przecinek -> kropka

  // Normalizacja zapisów pierwiastka
  const normalized = cleaned
    // \sqrt{...}, sqrt{...}, sqrt(...)
    .replace(/\\\\sqrt\{([^}]+)\}/g, "√$1")
    .replace(/\\sqrt\{([^}]+)\}/g, "√$1")
    .replace(/sqrt\{([^}]+)\}/g, "√$1")
    .replace(/sqrt\(([^)]+)\)/g, "√$1")
    // √{...}, √(...)
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1");

  try {
    const jsExpr = normalized
      // 2√3, 5√10 itd.
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      // samo √289
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);

    const val = Function(`"use strict"; return (${jsExpr});`)();
    if (typeof val !== "number" || !isFinite(val)) return NaN;
    return val;
  } catch {
    return NaN;
  }
}

// --- treść zadania ---

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { a, b, h } = task;

  return (
    <p>
      Dany jest prostopadłościan o krawędziach podstawy{" "}
      <InlineMath math={`a = ${a}\\,\\text{cm}`} /> i{" "}
      <InlineMath math={`b = ${b}\\,\\text{cm}`} /> oraz wysokości{" "}
      <InlineMath math={`h = ${h}\\,\\text{cm}`} />. Oblicz długość przekątnej
      tego prostopadłościanu. Wynik podaj w centymetrach.
    </p>
  );
}

// --- input ---
// ✅ jak w CubeDiagonalTask: label + suffixLatex, jednostka w podglądzie inputu

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  unwrapTask(taskOrWrapper);

  return (
    <div className="flex items-center gap-2">
      <InlineMath math="d =" />
      <SqrtInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="d"
        suffixLatex="\text{cm}"
      />
    </div>
  );
}

// --- podgląd odpowiedzi ucznia ---
// ✅ spójnie: "d = ... cm"

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";

  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const text = String(raw).trim();
  return <InlineMath math={`d = ${text}\\,\\text{cm}`} />;
}

// --- sprawdzanie odpowiedzi ---

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  const { d } = task; // dokładna długość przekątnej (może być niewymierna)

  if (!userAnswer) return false;

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw || String(raw).trim() === "") return false;

  const val = evaluateExpression(raw);
  if (typeof val !== "number" || !isFinite(val)) return false;

  // akceptujemy zarówno √(a^2+b^2+h^2), jak i przybliżenia liczbowe
  return Math.abs(val - d) < 1e-2;
}

// --- poprawna odpowiedź ---
// ✅ tylko postać z pierwiastkiem + jednostka (bez przybliżenia), InlineMath (do lewej)

// wyciąganie największego kwadratu spod pierwiastka: n = k^2 * m -> k√m
function simplifyRadical(n) {
  const N = Math.abs(Math.round(n));
  if (N === 0) return { coeff: 0, radicand: 1 };

  let bestCoeff = 1;
  let bestRadicand = N;

  const max = Math.floor(Math.sqrt(N));
  for (let i = max; i >= 2; i--) {
    const sq = i * i;
    if (N % sq === 0) {
      bestCoeff = i;
      bestRadicand = N / sq;
      break;
    }
  }

  return { coeff: bestCoeff, radicand: bestRadicand };
}

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { sum } = task;

  const { coeff, radicand } = simplifyRadical(sum);

  let latex;
  if (radicand === 1) {
    // liczba całkowita
    latex = `${coeff}\\,\\text{cm}`;
  } else if (coeff === 1) {
    // sam pierwiastek
    latex = `\\sqrt{${radicand}}\\,\\text{cm}`;
  } else {
    // uproszczona postać k√m
    latex = `${coeff}\\sqrt{${radicand}}\\,\\text{cm}`;
  }

  return <InlineMath math={latex} />;
}

// --- wyjaśnienie krok po kroku ---
// ✅ tu zostaje przybliżenie

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const { a, b, h, d } = task;

  const a2 = a * a;
  const b2 = b * b;
  const h2 = h * h;

  const dpSquared = a2 + b2;        // d_p^2
  const dp = Math.sqrt(dpSquared);  // długość d_p
  const isDpInteger = Number.isInteger(dp);

  const dSquared = dpSquared + h2;  // d^2
  const dApprox = Math.round(d * 100) / 100;

  // --- KROK 2: obliczanie d_p ---

  const step2Lines = [
    `d_p &= \\sqrt{a^2 + b^2}`,
    `&= \\sqrt{${a}^2 + ${b}^2}`,
    `&= \\sqrt{${a2} + ${b2}}`,
    `&= \\sqrt{${dpSquared}}`,
  ];

  if (isDpInteger) {
    step2Lines.push(`&= ${dp}`);
  }

  const step2Math =
    "\\begin{aligned}\n" +
    step2Lines.join(" \\\\ ") +
    "\n\\end{aligned}";

  // --- KROK 4: obliczanie d i uproszczenie pierwiastka ---

  const step4Lines = [];

  // start od wzoru
  step4Lines.push(`d &= \\sqrt{d_p^2 + h^2}`);

  if (isDpInteger) {
    step4Lines.push(`&= \\sqrt{${dp}^2 + ${h}^2}`);
    step4Lines.push(`&= \\sqrt{${dp * dp} + ${h2}}`);
  } else {
    step4Lines.push(`&= \\sqrt{${dpSquared} + ${h}^2}`);
    step4Lines.push(`&= \\sqrt{${dpSquared} + ${h2}}`);
  }

  step4Lines.push(`&= \\sqrt{${dSquared}}`);

  const { coeff, radicand } = simplifyRadical(dSquared);
  const canSimplify = !(coeff === 1 && radicand === dSquared);

  if (canSimplify) {
    let radicalExpr;
    if (radicand === 1) radicalExpr = `${coeff}`;
    else if (coeff === 1) radicalExpr = `\\sqrt{${radicand}}`;
    else radicalExpr = `${coeff}\\sqrt{${radicand}}`;
    step4Lines.push(`&= ${radicalExpr}`);
  }

  const step4Math =
    "\\begin{aligned}\n" +
    step4Lines.join(" \\\\ ") +
    "\n\\end{aligned}";

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong>{" "}
        Rozpatrujemy przekątną podstawy <InlineMath math="d_p" />.
        Podstawa jest prostokątem o bokach <InlineMath math="a" /> i{" "}
        <InlineMath math="b" />, więc:
        <BlockMath
          math={
            "\\begin{aligned}\n" +
            "d_p^2 &= a^2 + b^2 \\\\\n" +
            "d_p   &= \\sqrt{a^2 + b^2}\n" +
            "\\end{aligned}"
          }
        />
      </div>

      <div>
        <strong>Krok 2:</strong>{" "}
        Podstawiamy konkretne długości boków i obliczamy przekątną podstawy:
        <BlockMath math={step2Math} />
      </div>

      <div>
        <strong>Krok 3:</strong>{" "}
        Teraz przechodzimy do przekątnej prostopadłościanu <InlineMath math="d" />.
        Tworzy ona trójkąt prostokątny z <InlineMath math="d_p" /> oraz{" "}
        <InlineMath math="h" />:
        <BlockMath
          math={
            "\\begin{aligned}\n" +
            "d^2 &= d_p^2 + h^2 \\\\\n" +
            "d   &= \\sqrt{d_p^2 + h^2}\n" +
            "\\end{aligned}"
          }
        />
      </div>

      <div>
        <strong>Krok 4:</strong>{" "}
        Podstawiamy i liczymy:
        <BlockMath math={step4Math} />
        <BlockMath math={`d \\approx ${dApprox}\\,\\text{cm}`} />
      </div>
    </div>
  );
}
